import { LEGACY_SECTION_NAMES, Section } from './Section';
// Through the barrel, NOT './CustomMarker' directly. UserProfile is exported
// before PanelRange in index.ts, and CustomMarkerRange extends PanelRange at
// module-evaluation time — reaching that module directly from here makes this
// file the entry of the cycle and BloodworkRange evaluates against an undefined
// PanelRange. Nothing here needs the class until a constructor actually runs,
// by which point the barrel has finished. Same pattern as Bloodwork.ts.
import { CustomMarkerDefinition, CustomMarkerSection } from '.';
import { bwhLabsVisible } from '../utils/supplementsVisibility';
// Direct, not through the barrel: unitConversions imports nothing, so it can
// never participate in the cycle described above.
import { UnitSystem } from './units/unitConversions';

export type MarkersOutsideOptimalDisplay =
  | 'showBars'
  | 'hideBars'
  | 'hideSection'
  | 'minimizeSection';

export class UserProfile {
  id: string; // identity sub
  email: string;
  emailAlias: string;
  firstName: string; // Currently only available in PROD (different Auth strategy)
  lastName: string; // Currently only available in PROD (different Auth strategy)
  phoneNumber: string;
  userName: string;
  signupDate: string; // ISO-8601 format
  preparedBy?: string; // Optional field for "Prepared by" text

  // The heading on each PDF's cover page, set in Report Settings. Undefined (a
  // profile saved before the fields existed, or one that never changed them)
  // prints the shipped default — see resolveAnalysisReportTitle() and
  // resolveComparisonReportTitle() in models/reportTitles, which every reader
  // goes through rather than reading these directly.
  analysisReportTitle?: string;
  comparisonReportTitle?: string;

  // User Limitations
  numTests: number; // default is 0
  numTestsAllowed: number; // "unlimited" - the value should be 99,999 in DB, default is 2
  pdfExportAllowed: boolean;
  pdfExportComparisonTableAllowed: boolean;
  editClientAllowed: boolean;
  editTestResultsAllowed: boolean;
  showClientComparisonTable: boolean;
  showAnalyzeResultsComparison: boolean;
  uploadTestResults: boolean;
  showUploadTestResults: boolean;
  customLogoUrl: string;
  customLogoAllowed: boolean;

  // Entitlement for the custom-marker feature — My Markers and the per-test
  // "Add Your Own Markers" table.
  //
  // Defaults TRUE, and postSignUp sets it true, so every practitioner has it on
  // sign-up and it is turned OFF for anyone who doesn't take the paid plan.
  // That direction matters for the default: a profile saved before this field
  // existed has no value for it, and those practitioners must not be locked out
  // of markers they may already be using.
  customMarkersAllowed: boolean = true;
  enabledSupplementBrands: string[] = ['systemicFormulas'];

  // Brands the app has already auto-added to enabledSupplementBrands once (see
  // the constructor). A brand is seeded a single time so a newly-launched brand
  // reaches practitioners whose stored profile predates it; after that the
  // practitioner's own checkbox choice wins, including unchecking it.
  seededSupplementBrands: string[] = [];

  // Sections to exclude from the Analysis PDF by default. Empty (the default)
  // means every section is included. Each section header in Test Results Analysis
  // has its own "Include in PDF" checkbox; this list seeds those checkboxes on
  // mount. Per-test toggles are session-only and do not persist to this list.
  excludedFromPdfSections: Section[] = [];

  // Controls how the "Markers Outside Optimal" section renders by default:
  //   showBars        - the section is open, panels showing reference-range bars
  //                     (default)
  //   hideBars        - the section is open with each panel's charts collapsed;
  //                     user can click a panel's chevron to reveal its bars for
  //                     that session
  //   hideSection     - the entire section is hidden in the UI
  //   minimizeSection - the section renders collapsed to its header; clicking it
  //                     expands to the 'showBars' layout
  //
  // All four are screen-only. The PDF renders the section for every mode except
  // 'hideSection' (see bloodwork-analysis-pdf.tsx) — a minimized section still
  // exports in full, and the per-section "Include in PDF" checkbox is what
  // controls that.
  markersOutsideOptimalDisplay: MarkersOutsideOptimalDisplay = 'showBars';

  // When true (default), the Inflammation section hides cross-section
  // markers (those with a home in another section, e.g. ferritin, GGT,
  // albumin, HDL) unless they contribute a signal there. Markers native
  // to the section (hs-CRP, ESR, homocysteine) always stay visible
  // regardless of value. If filtering empties the section, it disappears
  // entirely.
  showOnlySectionSignals: boolean = true;

  // When true (default), the Immune section hides cross-section markers
  // unless they contribute a signal there. Example: alkaline phosphatase
  // is hidden when optimal or high (only low Alk Phos signals low zinc,
  // which the immune system needs); globulin is hidden when optimal.
  // Markers native to the section (WBC and differentials) always stay
  // visible regardless of value.
  showOnlyImmuneSectionSignals: boolean = true;

  // When true (default), the Immune section shows the absolute differential
  // bar graphs (Absolute Neutrophils, Lymphocytes, Monocytes, Eosinophils,
  // Basophils). Uncheck to hide those bar graphs. This is independent of
  // showOnlyImmuneSectionSignals, which never hides the differentials.
  showImmuneAbsolutes: boolean = true;

  // When true (default), a comparison report (current vs a previous test) also
  // renders reference-range bars for markers that have a value in the PREVIOUS
  // test but NOT the current one, plotting just the previous (blue) blood drop.
  // This gives a complete side-by-side even when the current test dropped a
  // marker — otherwise those markers vanish from the charts while still driving
  // the Results Overview section colors and thumbs. Uncheck to show only
  // markers present in the current test. No effect when viewing a single test.
  showPreviousTestMarkersInComparison: boolean = true;

  // The practitioner's default unit system for markers: 'US' conventional
  // (mg/dL, ng/mL) or 'SI' international (mmol/L, umol/L, g/L), which is how
  // labs report outside the US — Canada and Australia especially.
  //
  // A default, not a rule: the per-client Units radio on Add/Edit Client
  // overrides it, so a practice with clients in both systems is served. See
  // resolveUnitSystem() for the precedence.
  //
  // Display-only. Every value and every reference range is stored in US
  // conventional units regardless of this setting, and conversion happens at
  // the entry and display boundaries — see units/unitConversions.ts.
  unitSystem: UnitSystem = 'US';

  // The practitioner's own marker definitions ("My Markers", under the Test
  // Results menu), for markers LabSmarts does not ship yet. Offered as a pick
  // list on Add Test Results so the ranges are entered once, not per client.
  //
  // Private to this practitioner — nothing here reaches the shared
  // BloodworkRange catalog. Picking one COPIES it onto the test
  // (Bloodwork.customMarkers), so editing or deleting a definition never
  // changes a test that has already been saved.
  customMarkerLibrary: CustomMarkerDefinition[] = [];

  // Sections the practitioner named themselves, for panels LabSmarts has no
  // home for ("IgG Allergens", "Mycotoxins"). Offered alongside the shipped
  // sections wherever a custom marker is filed, and printed after them as plain
  // reference-range sections under the name given here (practitionerSectionTitle).
  //
  // A marker records the section's NAME, not its id, so renaming or deleting a
  // section here cannot retitle a report already issued to a client — the same
  // snapshot rule customMarkerLibrary follows.
  customMarkerSections: CustomMarkerSection[] = [];

  // The sections whose markers load onto EVERY new test, so a practitioner who
  // runs the same add-on panels does not rebuild the same table per client.
  //
  // Stores the section VALUE the dropdown uses ('' for the appendix, a Section
  // for a LabSmarts heading, 'practitioner:Name' for one of their own) rather
  // than a section id, because two of those three kinds have no id to store.
  //
  // Membership is resolved against customMarkerLibrary when a test is opened,
  // never stored here: a marker added to a defaulted section afterwards has to
  // join it with no second step, which a stored marker list could not do.
  defaultTestSections: string[] = [];

  // Individual markers that load onto every new test, by CustomMarkerDefinition
  // id.
  //
  // Separate from defaultTestSections, and a UNION with it: a practitioner who
  // runs a five-marker lipid panel but only ever adds NON HDL CHOLESTEROL has
  // no section to tick, and ticking the section would bring the other four.
  // Ids rather than names, because the name is the thing most likely to be
  // corrected and the id is what survives it.
  defaultTestMarkerIds: string[] = [];

  // Sections whose markers show their full reference range on Add Test Results
  // instead of folding to name, result and units.
  //
  // Folding suits a panel entered from memory; a practitioner who checks each
  // range as they type wants it in front of them, and clicking a caret per
  // marker is not an answer. Stored as section values, like defaultTestSections.
  expandedTestSections: string[] = [];

  canAddTest(): boolean {
    if (this.numTests < this.numTestsAllowed) {
      return true;
    }
    return false;
  }

  canUploadPdf(): boolean {
    return this.uploadTestResults;
  }

  canSeeUploadTestResults(): boolean {
    return this.showUploadTestResults;
  }

  canUploadCustomLogo(): boolean {
    return this.customLogoAllowed;
  }

  /**
   * Whether this practitioner can ADD custom markers. Existing definitions and
   * markers already on saved tests keep rendering either way — turning the
   * feature off must never blank a report that has already been issued.
   */
  canUseCustomMarkers(): boolean {
    return this.customMarkersAllowed !== false;
  }

  hasCustomLogo(): boolean {
    return (
      this.canUploadCustomLogo() &&
      !!this.customLogoUrl &&
      this.customLogoUrl.trim() !== ''
    );
  }

  getLogoUrl(): string {
    return this.canUploadCustomLogo() && this.hasCustomLogo()
      ? this.customLogoUrl
      : '/assets/imgs/logo.png';
  }

  incrementNumTests(): void {
    this.numTests++;
  }

  constructor(userProfile?: UserProfile) {
    // This handles copying the simple properties
    if (!userProfile) {
      return;
    }
    // This handles copying the simple properties
    Object.keys(userProfile).forEach(key => {
      this[key] = userProfile[key];
    });

    // Every profile stored before this field existed copies `undefined` over
    // the class default, so normalize rather than trusting the initializer.
    // Anything unrecognized reads as US, which is what those accounts see now.
    this.unitSystem = userProfile.unitSystem === 'SI' ? 'SI' : 'US';

    // What excludedFromPdfSections persists is the enum's VALUE, which for the
    // custom-marker section is client-facing copy that has now been renamed
    // three times (see Section.CUSTOM_MARKERS). A practitioner who turned that
    // section off under an older name has that older string stored; left as-is
    // it would match no section and the section would quietly reappear in the
    // client's PDF. Remap every retired name on load.
    //
    // No other Section shares these strings, so nothing else is caught by the
    // remap — the "Additional Markers" card in Add Test Results is a panel of
    // shipped markers, not a Section, and never reaches this list. Practitioner
    // sections are keyed off a frozen prefix (practitionerSectionStateKey) and
    // need no remap at all.
    const legacyBySection = new Map<string, Section>();
    Object.keys(LEGACY_SECTION_NAMES).forEach(current =>
      LEGACY_SECTION_NAMES[current].forEach(legacy =>
        legacyBySection.set(legacy, current as Section),
      ),
    );
    this.excludedFromPdfSections = Array.isArray(
      userProfile.excludedFromPdfSections,
    )
      ? userProfile.excludedFromPdfSections.map(
          section => legacyBySection.get(section as string) || section,
        )
      : [];

    // Rehydrate library definitions as real instances, and tolerate a profile
    // saved before this field existed (or any non-array shape).
    this.customMarkerLibrary = Array.isArray(userProfile.customMarkerLibrary)
      ? userProfile.customMarkerLibrary.map(
          (definition: any) => new CustomMarkerDefinition(definition),
        )
      : [];

    this.customMarkerSections = Array.isArray(userProfile.customMarkerSections)
      ? userProfile.customMarkerSections
          .map((section: any) => new CustomMarkerSection(section))
          .filter(section => !!(section.id && (section.name || '').trim()))
      : [];

    // Tolerates a profile saved before this field existed, and drops anything
    // that is not a usable section value — a stray entry would otherwise load
    // nothing onto every test forever, silently.
    this.defaultTestSections = Array.isArray(userProfile.defaultTestSections)
      ? userProfile.defaultTestSections.filter(
          (value: any) => typeof value === 'string',
        )
      : [];

    this.defaultTestMarkerIds = Array.isArray(userProfile.defaultTestMarkerIds)
      ? userProfile.defaultTestMarkerIds.filter(
          (value: any) => typeof value === 'string',
        )
      : [];

    this.expandedTestSections = Array.isArray(userProfile.expandedTestSections)
      ? userProfile.expandedTestSections.filter(
          (value: any) => typeof value === 'string',
        )
      : [];

    // BWH Labs is a pre-launch brand, on hold until the partnership and its
    // affiliate code are finalized: hidden everywhere today, local dev included
    // (see bwhLabsVisible()), mirroring the Supplement Settings block.
    // Because any prior profile save persists enabledSupplementBrands, the
    // field default can't be relied on to reach existing profiles — so
    // normalize on load:
    //   hold on (today) → strip 'bwhLabs' so it never renders, even if a stored
    //                     profile happens to carry it (dev and slava profiles
    //                     do: the brand shipped there before this hold).
    //   hold lifted     → seed 'bwhLabs' ONCE so it renders for every
    //                     practitioner
    // The seed is one-time and recorded in seededSupplementBrands: re-adding it
    // on every load would silently undo a practitioner who unchecks the brand,
    // so once seeded the stored enabledSupplementBrands is left alone.
    //
    // Stripping also clears that seed marker, which matters for the profiles
    // seeded while the brand still shipped: leaving the marker behind would
    // mean nobody gets re-seeded when the hold lifts — every practitioner
    // would find the brand silently unchecked at launch.
    const showBwhLabs = bwhLabsVisible();
    if (!this.enabledSupplementBrands) {
      this.enabledSupplementBrands = ['systemicFormulas'];
    }
    if (!this.seededSupplementBrands) {
      this.seededSupplementBrands = [];
    }
    if (showBwhLabs) {
      if (
        !this.enabledSupplementBrands.includes('bwhLabs') &&
        !this.seededSupplementBrands.includes('bwhLabs')
      ) {
        this.enabledSupplementBrands = [
          ...this.enabledSupplementBrands,
          'bwhLabs',
        ];
      }
      if (!this.seededSupplementBrands.includes('bwhLabs')) {
        this.seededSupplementBrands = [
          ...this.seededSupplementBrands,
          'bwhLabs',
        ];
      }
    } else {
      this.enabledSupplementBrands = this.enabledSupplementBrands.filter(
        b => b !== 'bwhLabs',
      );
      this.seededSupplementBrands = this.seededSupplementBrands.filter(
        b => b !== 'bwhLabs',
      );
    }
  }
}
